// Patient balances — the backlog worklist (outreach PR 3). Who owes a balance older
// than N days, when they were last told, whether they promised to pay, and what came
// in since; a bulk text or email to the selected rows as a throttled background job
// with progress and a per-campaign summary.
//
// Registry page (features/registry.js, `feature: 'collections'`, staff only); App.js
// renders <FeatureOff/> when the feature is off, and the Sidebar never offers the page
// to the client role (not in CLIENT_PAGES). A read-only viewer who lands here anyway is
// told so and nothing is fetched: the routes refuse the role too.
//
// Three rules the page keeps for the server, not instead of it:
//
// 1. NOTHING SENDS WITHOUT A PREVIEW. "Start campaign" is disabled until the server
//    has rendered the first three messages for exactly this channel, template and
//    set of recipients; changing any of them drops the preview. The start posts the
//    same choice plus `confirm: true`.
// 2. A REFUSAL IS THE SERVER'S SENTENCE. The daily cap (409), a campaign already
//    running (409), a bad rate or template (400) and "campaigns unavailable until the
//    migration" (503) are shown verbatim and dismissed by hand.
// 3. THE JOB IS THE SERVER'S. This page only reads progress (every 2.5 s while the
//    campaign is queued or running), cancels, and resumes a stalled one. It never
//    re-sends anything itself.
//
// Contract: docs/outreach.md "Patient balances" / "Campaigns". Amounts are integer
// cents from the server.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatPatientName } from "../../utils/formatName";
import ExportButton from "../reports/ExportButton";
import {
  CHANNELS,
  DEFAULT_FILTERS,
  POLL_MS,
  RATE_DEFAULT,
  RATE_MAX,
  RATE_MIN,
  balancesQueryParams,
  campaignStatusChip,
  cancelCampaign,
  centsToDollars,
  channelLabel,
  dollarsToCents,
  fetchBalances,
  fetchCampaign,
  fetchCampaigns,
  fetchStatus,
  fetchTemplates,
  formatCents,
  formatDay,
  formatWhen,
  isCampaignActive,
  isCampaignFinished,
  parseDays,
  parseRate,
  previewCampaign,
  promiseChip,
  resumeCampaign,
  sendStatusChip,
  startCampaign,
} from "./api";
import "./PatientBalances.css";

const FILTER_DEBOUNCE_MS = 400;
const PREVIEW_COUNT = 3;

function Message({ tone, children, onClose, testId }) {
  return (
    <div
      className={`pb-message ${tone}`}
      role={tone === "error" ? "alert" : undefined}
      data-testid={testId}
    >
      <span>{children}</span>
      {onClose && (
        <button
          type="button"
          className="pb-message-close"
          aria-label="Dismiss"
          onClick={onClose}
        >
          ×
        </button>
      )}
    </div>
  );
}

function Chip({ tone = "muted", title, children }) {
  return (
    <span className={`pb-chip ${tone}`} title={title || undefined}>
      {children}
    </span>
  );
}

// A row's channel availability as the server states it: a chip per channel that
// exists, muted with the reason when it may not be used.
function ChannelChips({ row }) {
  return (
    <span className="pb-channels">
      {row.hasMobile && (
        <Chip
          tone={row.canText ? "good" : "muted"}
          title={row.canText ? "Can be texted" : row.canTextReason || "Cannot be texted"}
        >
          {row.canText ? "Text" : "Text ✕"}
        </Chip>
      )}
      {row.hasEmail && (
        <Chip
          tone={row.canEmail ? "good" : "muted"}
          title={row.canEmail ? "Can be emailed" : row.canEmailReason || "Cannot be emailed"}
        >
          {row.canEmail ? "Email" : "Email ✕"}
        </Chip>
      )}
      {!row.hasMobile && !row.hasEmail && <Chip tone="warn">No channel</Chip>}
      {row.optedOut && <Chip tone="bad">Opted out</Chip>}
    </span>
  );
}

// The choice a preview was built from. Any change to it drops the preview, so Start
// can never post a campaign the biller has not seen three messages of.
const choiceKey = ({ channel, templateId, patientIds }) =>
  JSON.stringify([channel, templateId == null ? "" : String(templateId), patientIds]);

// Pick a channel, a template and a rate; preview the first three; start. On 202 the
// parent swaps this for the progress panel.
function CampaignModal({ practiceId, rows, status, filters, onClose, onStarted }) {
  const textable = rows.filter((r) => r.canText).length;
  const emailable = rows.filter((r) => r.canEmail).length;
  const patientIds = useMemo(() => rows.map((r) => r.patientId), [rows]);

  const channelState = useMemo(() => {
    const sms = { enabled: true, reason: null };
    const email = { enabled: true, reason: null };
    if (!status.smsConfigured) {
      sms.enabled = false;
      sms.reason = "SMS not configured";
    } else if (textable === 0) {
      sms.enabled = false;
      sms.reason = "None of the selected patients can be texted";
    }
    if (!status.emailConfigured) {
      email.enabled = false;
      email.reason = "Email not configured";
    } else if (emailable === 0) {
      email.enabled = false;
      email.reason = "None of the selected patients can be emailed";
    }
    return { sms, email };
  }, [status, textable, emailable]);

  const [channel, setChannel] = useState(() =>
    channelState.sms.enabled || !channelState.email.enabled ? "sms" : "email",
  );
  const [templates, setTemplates] = useState({}); // kind -> rows
  const [templatesError, setTemplatesError] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [rateText, setRateText] = useState(String(RATE_DEFAULT));
  const [preview, setPreview] = useState(null); // {key, messages}
  const [previewing, setPreviewing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (templates[channel]) return undefined;
    let cancelled = false;
    setTemplatesError(null);
    fetchTemplates(practiceId, channel)
      .then((list) => {
        if (cancelled) return;
        const usable = list.filter((t) => t.enabled);
        setTemplates((t) => ({ ...t, [channel]: usable }));
        // Default the first: the practice's own templates come before the defaults.
        setTemplateId((current) =>
          current || (usable.length > 0 ? String(usable[0].id) : ""),
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setTemplates((t) => ({ ...t, [channel]: [] }));
          setTemplatesError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, channel, templates]);

  const list = templates[channel] || [];
  const rate = parseRate(rateText);
  const rateInvalid = Number.isNaN(rate);
  const key = choiceKey({ channel, templateId, patientIds });
  const previewCurrent = preview && preview.key === key;
  const reachable = channel === "sms" ? textable : emailable;
  const canPreview =
    channelState[channel].enabled && !!templateId && patientIds.length > 0;
  const canStart = previewCurrent && canPreview && !rateInvalid;

  const pickChannel = (next) => {
    if (next === channel || !channelState[next].enabled) return;
    setChannel(next);
    setTemplateId("");
    setPreview(null);
    setError(null);
  };

  const runPreview = async () => {
    if (inFlight.current || !canPreview) return;
    inFlight.current = true;
    setPreviewing(true);
    setError(null);
    try {
      const messages = await previewCampaign(practiceId, {
        channel,
        templateId,
        patientIds: patientIds.slice(0, PREVIEW_COUNT),
      });
      setPreview({ key, messages });
    } catch (err) {
      setPreview(null);
      setError(err.message);
    } finally {
      inFlight.current = false;
      setPreviewing(false);
    }
  };

  const runStart = async () => {
    if (inFlight.current || !canStart) return;
    inFlight.current = true;
    setStarting(true);
    setError(null);
    try {
      const campaign = await startCampaign(practiceId, {
        channel,
        templateId,
        patientIds,
        ratePerSec: rate,
        filters,
      });
      onStarted(campaign);
    } catch (err) {
      // 409 (cap, already running), 400 (rate, template, ids) and 503 carry the
      // server's own sentence. A refusal invalidates nothing on screen, but the
      // preview is dropped so a changed cap is re-read before another try.
      setError(err.message);
      if (err.status === 409 || err.status === 503) setPreview(null);
    } finally {
      inFlight.current = false;
      setStarting(false);
    }
  };

  const busy = previewing || starting;
  const neither = !channelState.sms.enabled && !channelState.email.enabled;

  return (
    <div className="pb-modal" data-testid="campaign-modal">
      <div className="pb-modal-header">
        <h2>Send to {rows.length} selected</h2>
        <button
          type="button"
          className="pb-modal-close"
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
        >
          ×
        </button>
      </div>
      <div className="pb-modal-body">
        <p className="pb-modal-lede">
          One message per selected patient, from the template, sent one at a time at the
          rate below as a background job. The server re-checks each patient's balance and
          consent as it goes, and stops at the practice's daily cap. Nothing is sent until
          you have previewed and clicked Start.
        </p>

        <div className="pb-channels-row" role="group" aria-label="Channel">
          <div className="pb-tabs">
            {CHANNELS.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`pb-tab${channel === c.key ? " active" : ""}`}
                onClick={() => pickChannel(c.key)}
                disabled={!channelState[c.key].enabled || busy}
                aria-pressed={channel === c.key}
                title={channelState[c.key].reason || `Send a ${c.label}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <span className="pb-channel-notes">
            {!channelState.sms.enabled && (
              <span className="pb-channel-note">{channelState.sms.reason}</span>
            )}
            {!channelState.email.enabled && (
              <span className="pb-channel-note">{channelState.email.reason}</span>
            )}
            {status.dryRun && (
              <Chip tone="warn" title="TWILIO_DRY_RUN is on: messages are recorded, not delivered">
                Dry run
              </Chip>
            )}
          </span>
        </div>

        {!neither && (
          <p className="pb-muted" data-testid="campaign-reachable">
            {reachable} of {rows.length} selected can be reached by {channelLabel(channel)};
            the rest are skipped and counted.
          </p>
        )}

        {error && (
          <Message tone="error" onClose={() => setError(null)} testId="campaign-error">
            {error}
          </Message>
        )}

        {!neither && (
          <>
            <div className="pb-form-row">
              <label className="pb-field">
                <span>Template</span>
                <select
                  name="template_id"
                  value={templateId}
                  onChange={(e) => {
                    setTemplateId(e.target.value);
                    setPreview(null);
                  }}
                  disabled={busy}
                >
                  {list.length === 0 && (
                    <option value="">
                      {templates[channel] ? "No templates" : "Loading templates…"}
                    </option>
                  )}
                  {list.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                      {t.scope === "global" ? " (default)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="pb-field pb-field-rate">
                <span>Messages per second</span>
                <input
                  type="text"
                  inputMode="decimal"
                  name="rate_per_sec"
                  className={rateInvalid ? "is-invalid" : ""}
                  value={rateText}
                  onChange={(e) => setRateText(e.target.value)}
                  disabled={busy}
                />
                <small className="pb-hint">
                  {RATE_MIN} to {RATE_MAX}; the default is {RATE_DEFAULT}.
                </small>
              </label>
            </div>
            {templatesError && <Message tone="error">{templatesError}</Message>}
            {rateInvalid && (
              <span className="pb-field-error">
                Enter a rate between {RATE_MIN} and {RATE_MAX} messages per second
              </span>
            )}

            {previewCurrent && (
              <div className="pb-preview" data-testid="campaign-preview">
                <div className="pb-preview-head">
                  <strong>
                    Preview · first {preview.messages.length} of {rows.length}
                  </strong>
                </div>
                {preview.messages.length === 0 && (
                  <p className="pb-muted">The server rendered no messages.</p>
                )}
                {preview.messages.map((m, i) => (
                  <div
                    key={`${m.patientId}-${i}`}
                    className="pb-preview-message"
                    data-testid="campaign-preview-message"
                  >
                    <div className="pb-preview-meta">
                      <span className="pb-address">to {m.toAddressMasked || "—"}</span>
                      {!m.canSend && (
                        <Chip tone="warn" title={m.reason || undefined}>
                          Will be skipped
                        </Chip>
                      )}
                    </div>
                    {m.subject && <p className="pb-subject">{m.subject}</p>}
                    <p className="pb-body">{m.body}</p>
                    {!m.canSend && m.reason && <p className="pb-reason">{m.reason}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div className="pb-modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={runPreview}
          disabled={!canPreview || busy}
          title={
            canPreview
              ? "Render the first three messages as the patients will read them"
              : "Pick a channel and a template first"
          }
        >
          {previewing ? "Rendering…" : "Preview"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={runStart}
          disabled={!canStart || busy}
          title={
            canStart
              ? `Start sending ${rows.length} ${channelLabel(channel)} message${rows.length === 1 ? "" : "s"}`
              : previewCurrent
                ? "Fix the rate first"
                : "Preview the messages before starting"
          }
        >
          {starting ? "Starting…" : `Start campaign (${rows.length})`}
        </button>
      </div>
    </div>
  );
}

// The progress panel: polls one campaign while it is queued or running, offers
// Cancel (and Resume when stalled), and shows the summary once it has finished.
function CampaignPanel({ practiceId, campaignId, initial, onClose, onChanged }) {
  const [data, setData] = useState(() =>
    initial
      ? {
          campaign: initial,
          progress: {
            total: initial.total,
            sent: initial.sent,
            failed: initial.failed,
            skipped: initial.skipped,
            pending: Math.max(
              initial.total - initial.sent - initial.failed - initial.skipped,
              0,
            ),
            delivered: 0,
          },
          summary: null,
        }
      : null,
  );
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = (ticket.current += 1);
    try {
      const body = await fetchCampaign(practiceId, campaignId);
      if (mine !== ticket.current) return null;
      setData(body);
      setError(null);
      return body;
    } catch (err) {
      if (mine === ticket.current) setError(err.message);
      return null;
    }
  }, [practiceId, campaignId]);

  // One read now, then one every POLL_MS while the server says the job is live. The
  // timer is re-armed from the result, never from the clock alone, so a slow answer
  // cannot queue two reads.
  const status = data ? data.campaign.status : null;
  const live = status === null || isCampaignActive(status);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      const body = await load();
      if (cancelled) return;
      if (body && isCampaignActive(body.campaign.status)) {
        timer = setTimeout(tick, POLL_MS);
      } else if (body && onChanged) {
        onChanged();
      }
    };
    if (live) tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      ticket.current += 1;
    };
    // `live` flips once when the job finishes; the effect must not re-arm then.
    // `onChanged` is deliberately not a dependency: it is a refresh of the parent's
    // list, and re-arming the poll on its identity would double-read the campaign.
  }, [load, live]);

  const runAction = async (fn, fallback) => {
    if (acting) return;
    setActing(true);
    setActionError(null);
    try {
      await fn(practiceId, campaignId);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setActionError(err.message || fallback);
    } finally {
      setActing(false);
    }
  };

  const campaign = data ? data.campaign : null;
  const progress = data ? data.progress : null;
  const summary = data ? data.summary : null;
  const chip = campaign ? campaignStatusChip(campaign.status) : null;
  const done = progress ? progress.sent + progress.failed + progress.skipped : 0;
  const pct = progress && progress.total > 0 ? Math.min(100, Math.round((done / progress.total) * 100)) : 0;
  const finished = campaign ? isCampaignFinished(campaign.status) : false;
  const stalled = campaign ? campaign.status === "stalled" : false;
  const canCancel = campaign ? isCampaignActive(campaign.status) || stalled : false;
  const canResume =
    campaign &&
    (stalled || (campaign.status === "failed" && progress && progress.pending > 0));

  return (
    <div className="pb-modal" data-testid="campaign-panel">
      <div className="pb-modal-header">
        <h2>
          Campaign #{campaignId}
          {campaign && (
            <>
              {" "}
              <Chip tone={chip.tone}>{chip.label}</Chip>
              {campaign.dryRun && (
                <Chip tone="warn" title="Recorded, not delivered">
                  Dry run
                </Chip>
              )}
            </>
          )}
        </h2>
        <button type="button" className="pb-modal-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="pb-modal-body">
        {error && <Message tone="error">{error}</Message>}
        {!error && !data && <p className="pb-muted">Loading…</p>}
        {campaign && (
          <>
            <dl className="pb-facts">
              <dt>Channel</dt>
              <dd>{channelLabel(campaign.channel)}</dd>
              <dt>Template</dt>
              <dd>{campaign.templateName || "—"}</dd>
              <dt>Rate</dt>
              <dd>{campaign.ratePerSec}/s</dd>
              <dt>Started</dt>
              <dd>{campaign.startedAt ? formatWhen(campaign.startedAt) : "not yet"}</dd>
              {campaign.finishedAt && (
                <>
                  <dt>Finished</dt>
                  <dd>{formatWhen(campaign.finishedAt)}</dd>
                </>
              )}
              {campaign.createdBy && (
                <>
                  <dt>By</dt>
                  <dd>{campaign.createdBy}</dd>
                </>
              )}
            </dl>

            {campaign.error && (
              <Message tone={campaign.status === "failed" ? "error" : "warn"}>
                {campaign.error}
              </Message>
            )}
            {stalled && (
              <Message tone="warn">
                No heartbeat for over two minutes. Resume restarts the job; a recipient the
                job was mid-send on is marked unknown and never sent again.
              </Message>
            )}

            {progress && (
              <div className="pb-progress" data-testid="campaign-progress">
                <div
                  className="pb-bar"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                >
                  <div className="pb-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="pb-counts">
                  <span>
                    <strong>{done}</strong> of <strong>{progress.total}</strong>
                  </span>
                  <span>Sent {progress.sent}</span>
                  <span>Delivered {progress.delivered}</span>
                  <span>Failed {progress.failed}</span>
                  <span>Skipped {progress.skipped}</span>
                  <span>Pending {progress.pending}</span>
                </div>
              </div>
            )}

            {finished && summary && (
              <div className="pb-summary" data-testid="campaign-summary">
                <h3>Summary</h3>
                <div className="pb-stats">
                  <div className="pb-stat">
                    <span className="pb-stat-num">{summary.sent}</span>
                    <span className="pb-stat-label">Sent</span>
                  </div>
                  <div className="pb-stat">
                    <span className="pb-stat-num">{summary.delivered}</span>
                    <span className="pb-stat-label">Delivered</span>
                  </div>
                  <div className={`pb-stat${summary.failed > 0 ? " bad" : ""}`}>
                    <span className="pb-stat-num">{summary.failed}</span>
                    <span className="pb-stat-label">Failed</span>
                  </div>
                  <div className="pb-stat">
                    <span className="pb-stat-num">{summary.skippedOptout}</span>
                    <span className="pb-stat-label">Opted out (skipped)</span>
                  </div>
                  <div className="pb-stat">
                    <span className="pb-stat-num">{summary.replies}</span>
                    <span className="pb-stat-label">Replies</span>
                  </div>
                  <div className="pb-stat">
                    <span className="pb-stat-num">{summary.optedOut}</span>
                    <span className="pb-stat-label">Opted out after</span>
                  </div>
                  <div className={`pb-stat${summary.paymentsCents > 0 ? " good" : ""}`}>
                    <span className="pb-stat-num">{formatCents(summary.paymentsCents)}</span>
                    <span className="pb-stat-label">
                      Payments ({summary.paymentsCount})
                    </span>
                  </div>
                </div>
                <p className="pb-muted" data-testid="campaign-skipped">
                  Skipped: {summary.skippedNoBalance} with no balance left,{" "}
                  {summary.skippedCap} at the daily cap, {summary.skippedDuplicate} duplicate
                  (same mobile or email as another selected patient), {summary.cancelled}{" "}
                  cancelled, {summary.unknown} unknown (left mid-send; never re-sent).
                  {summary.windowEnd
                    ? ` Replies, opt-outs and payments are counted until ${formatWhen(summary.windowEnd)}.`
                    : ""}
                </p>
              </div>
            )}

            {actionError && (
              <Message tone="error" onClose={() => setActionError(null)}>
                {actionError}
              </Message>
            )}
          </>
        )}
      </div>
      <div className="pb-modal-footer">
        {canResume && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => runAction(resumeCampaign, "The campaign could not be resumed.")}
            disabled={acting}
            title="Restart the job for the recipients still pending"
          >
            {acting ? "Working…" : "Resume"}
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            className="btn pb-danger"
            onClick={() => runAction(cancelCampaign, "The campaign could not be cancelled.")}
            disabled={acting}
            title="Skip every recipient not yet sent; the job stops at its next check"
          >
            {acting ? "Working…" : "Cancel campaign"}
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {finished ? "Done" : "Close"}
        </button>
      </div>
    </div>
  );
}

function CampaignsList({ campaigns, error, onOpen }) {
  return (
    <section className="pb-campaigns" data-testid="campaigns">
      <h2>Campaigns</h2>
      {error && <Message tone="error">{error}</Message>}
      {!error && campaigns === null && <p className="pb-muted">Loading…</p>}
      {campaigns && campaigns.length === 0 && (
        <p className="pb-empty">No campaign has been sent from this page yet.</p>
      )}
      {campaigns && campaigns.length > 0 && (
        <table className="pb-mini-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              <th>Channel</th>
              <th>Template</th>
              <th className="num">Total</th>
              <th className="num">Sent</th>
              <th className="num">Failed</th>
              <th className="num">Skipped</th>
              <th>Started</th>
              <th>Finished</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => {
              const chip = campaignStatusChip(c.status);
              return (
                <tr key={c.id} data-testid="campaign-row">
                  <td>{c.id}</td>
                  <td>
                    <Chip tone={chip.tone} title={c.error || undefined}>
                      {chip.label}
                    </Chip>
                    {c.dryRun && (
                      <>
                        {" "}
                        <Chip tone="warn">Dry run</Chip>
                      </>
                    )}
                  </td>
                  <td>{channelLabel(c.channel)}</td>
                  <td>{c.templateName || "—"}</td>
                  <td className="num">{c.total}</td>
                  <td className="num">{c.sent}</td>
                  <td className="num">{c.failed}</td>
                  <td className="num">{c.skipped}</td>
                  <td className="pb-nowrap">{c.startedAt ? formatWhen(c.startedAt) : "—"}</td>
                  <td className="pb-nowrap">{c.finishedAt ? formatWhen(c.finishedAt) : "—"}</td>
                  <td className="pb-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => onOpen(c)}
                    >
                      {isCampaignActive(c.status) || c.status === "stalled" ? "Progress" : "Summary"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function PatientBalancesPage({
  selectedPractice,
  features = {},
  readOnly = false,
}) {
  const practiceId = selectedPractice?.id;

  // What is typed, and what has been applied (debounced, validated).
  const [minBalanceText, setMinBalanceText] = useState(
    (DEFAULT_FILTERS.minBalanceCents / 100).toFixed(2),
  );
  const [oldestDosText, setOldestDosText] = useState(
    String(DEFAULT_FILTERS.oldestDosBeforeDays),
  );
  const [lastOutreachText, setLastOutreachText] = useState(
    String(DEFAULT_FILTERS.lastOutreachBeforeDays),
  );
  const [minBalanceCents, setMinBalanceCents] = useState(DEFAULT_FILTERS.minBalanceCents);
  const [oldestDosBeforeDays, setOldestDosBeforeDays] = useState(
    DEFAULT_FILTERS.oldestDosBeforeDays,
  );
  const [lastOutreachBeforeDays, setLastOutreachBeforeDays] = useState(
    DEFAULT_FILTERS.lastOutreachBeforeDays,
  );
  const [hasMobile, setHasMobile] = useState(DEFAULT_FILTERS.hasMobile);
  const [hasEmail, setHasEmail] = useState(DEFAULT_FILTERS.hasEmail);
  const [includeOptedOut, setIncludeOptedOut] = useState(DEFAULT_FILTERS.includeOptedOut);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [status, setStatus] = useState(null); // fetchStatus: configured flags, dry run
  const [statusError, setStatusError] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [campaignsError, setCampaignsError] = useState(null);
  // null | {mode: "compose"} | {mode: "progress", campaignId, initial}
  const [dialog, setDialog] = useState(null);

  // Only the newest request may write, so a practice switch or a quick change of
  // filter cannot paint one list under another's filters.
  const ticket = useRef(0);
  // The practice on screen now. A write that finishes after a practice switch calls
  // the handlers of the render it started in; they compare against this so the old
  // practice's list never paints under the new one.
  const practiceRef = useRef(practiceId);
  practiceRef.current = practiceId;

  const minBalanceInvalid = Number.isNaN(dollarsToCents(minBalanceText));
  const oldestDosInvalid = Number.isNaN(parseDays(oldestDosText));
  const lastOutreachInvalid = Number.isNaN(parseDays(lastOutreachText));

  useEffect(() => {
    const timer = setTimeout(() => {
      const cents = dollarsToCents(minBalanceText);
      if (!Number.isNaN(cents)) setMinBalanceCents(cents ?? 0);
      const dos = parseDays(oldestDosText);
      if (!Number.isNaN(dos)) setOldestDosBeforeDays(dos ?? 0);
      const out = parseDays(lastOutreachText);
      if (!Number.isNaN(out)) setLastOutreachBeforeDays(out ?? 0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [minBalanceText, oldestDosText, lastOutreachText]);

  const filters = useMemo(
    () => ({
      minBalanceCents,
      oldestDosBeforeDays,
      lastOutreachBeforeDays,
      hasMobile,
      hasEmail,
      includeOptedOut,
    }),
    [
      minBalanceCents,
      oldestDosBeforeDays,
      lastOutreachBeforeDays,
      hasMobile,
      hasEmail,
      includeOptedOut,
    ],
  );

  useEffect(() => {
    setData(null);
    setSelected(new Set());
    setDialog(null);
    setMessage(null);
    setCampaigns(null);
    setCampaignsError(null);
    setStatus(null);
    setStatusError(null);
  }, [practiceId]);

  const load = useCallback(async () => {
    if (readOnly || !practiceId || practiceId !== practiceRef.current) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchBalances(practiceId, filters);
      if (mine !== ticket.current || practiceId !== practiceRef.current) return;
      setData(body);
      // A row that left the list is no longer selected.
      setSelected((prev) => {
        const ids = new Set(body.rows.map((r) => r.patientId));
        const next = new Set([...prev].filter((id) => ids.has(id)));
        return next.size === prev.size ? prev : next;
      });
    } catch (err) {
      if (mine !== ticket.current || practiceId !== practiceRef.current) return;
      setData(null);
      setError(err.message);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, filters, readOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCampaigns = useCallback(async () => {
    if (readOnly || !practiceId) return;
    try {
      const list = await fetchCampaigns(practiceId);
      if (practiceId !== practiceRef.current) return;
      setCampaigns(list);
      setCampaignsError(null);
    } catch (err) {
      if (practiceId !== practiceRef.current) return;
      setCampaigns((c) => c || []);
      setCampaignsError(err.message);
    }
  }, [practiceId, readOnly]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (readOnly || !practiceId) return undefined;
    let cancelled = false;
    fetchStatus(practiceId)
      .then((st) => {
        if (!cancelled) setStatus(st);
      })
      .catch((err) => {
        // Only an explicit true configures a channel: no answer offers none.
        if (!cancelled) {
          setStatus({
            smsConfigured: false,
            emailConfigured: false,
            dryRun: false,
            practicePhone: null,
          });
          setStatusError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, readOnly]);

  if (!practiceId) {
    return (
      <div className="pb-page">
        <p className="pb-empty">Select a practice to see who owes a balance.</p>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="pb-page">
        <h1>Patient balances</h1>
        <p className="pb-empty" data-testid="pb-staff-only">
          Patient balances is a staff page. Nothing on it is available in the client view.
        </p>
      </div>
    );
  }

  const rows = data ? data.rows : [];
  // A row with a channel the server allows may be selected; select-all covers these.
  const reachable = rows.filter((r) => r.canText || r.canEmail);
  const allSelected = reachable.length > 0 && reachable.every((r) => selected.has(r.patientId));
  const selectedRows = rows.filter((r) => selected.has(r.patientId));
  const selectedBalance = selectedRows.reduce((sum, r) => sum + r.balanceCents, 0);
  const anyFilterOff =
    filters.minBalanceCents !== DEFAULT_FILTERS.minBalanceCents ||
    filters.oldestDosBeforeDays !== DEFAULT_FILTERS.oldestDosBeforeDays ||
    filters.lastOutreachBeforeDays !== DEFAULT_FILTERS.lastOutreachBeforeDays ||
    filters.hasMobile ||
    filters.hasEmail ||
    filters.includeOptedOut;

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(reachable.map((r) => r.patientId)));
  };

  const onStarted = (campaign) => {
    if (practiceId !== practiceRef.current) return;
    setDialog({ mode: "progress", campaignId: campaign.id, initial: campaign });
    setSelected(new Set());
    setMessage({
      type: "success",
      text: `Campaign #${campaign.id} started for ${campaign.total} patient${campaign.total === 1 ? "" : "s"}${campaign.dryRun ? " (dry run)" : ""}.`,
    });
    loadCampaigns();
  };

  const closeDialog = () => {
    const wasProgress = dialog && dialog.mode === "progress";
    setDialog(null);
    if (wasProgress) {
      // Payments and outreach dates moved while the job ran.
      loadCampaigns();
      load();
    }
  };

  return (
    <div className="pb-page">
      <div className="pb-header">
        <div>
          <h1>Patient balances</h1>
          <p className="pb-sub">
            Who owes {selectedPractice.name} on visits older than the days below, when they
            were last told, whether they promised to pay, and what has come in since.
            Sending is a campaign: pick the rows, pick a template, preview, start.
          </p>
        </div>
        <div className="pb-header-actions">
          {status && status.dryRun && (
            <Chip tone="warn" title="TWILIO_DRY_RUN is on: messages are recorded, not delivered">
              Dry run
            </Chip>
          )}
          {/* The route is W1a's and audited there; this is the button. It exports
              THIS list with THESE filters through the same query builder, with no
              phone or email column. */}
          {features.exports && (
            <ExportButton
              practiceId={practiceId}
              kind="patient-balances"
              readOnly={readOnly}
              getParams={() => balancesQueryParams(filters, { forExport: true })}
            />
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {message && (
        <Message tone={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Message>
      )}
      {statusError && (
        <Message tone="warn" onClose={() => setStatusError(null)}>
          {statusError}
        </Message>
      )}

      <div className="pb-filters" data-testid="pb-filters">
        <label className="pb-inline-field">
          <span>Balance at least $</span>
          <input
            type="text"
            inputMode="decimal"
            name="min_balance"
            className={minBalanceInvalid ? "is-invalid" : ""}
            value={minBalanceText}
            placeholder="25.00"
            onChange={(e) => setMinBalanceText(e.target.value)}
          />
        </label>
        <label className="pb-inline-field">
          <span>Oldest DOS older than</span>
          <input
            type="text"
            inputMode="numeric"
            name="oldest_dos_days"
            className={`pb-days${oldestDosInvalid ? " is-invalid" : ""}`}
            value={oldestDosText}
            onChange={(e) => setOldestDosText(e.target.value)}
          />
          <span>days</span>
        </label>
        <label className="pb-inline-field">
          <span>Last outreach older than</span>
          <input
            type="text"
            inputMode="numeric"
            name="last_outreach_days"
            className={`pb-days${lastOutreachInvalid ? " is-invalid" : ""}`}
            value={lastOutreachText}
            onChange={(e) => setLastOutreachText(e.target.value)}
            title="0 means any; a patient never contacted counts as older than any number"
          />
          <span>days</span>
        </label>
        <label className="pb-check">
          <input
            type="checkbox"
            name="has_mobile"
            checked={hasMobile}
            onChange={(e) => setHasMobile(e.target.checked)}
          />
          <span>Has mobile</span>
        </label>
        <label className="pb-check">
          <input
            type="checkbox"
            name="has_email"
            checked={hasEmail}
            onChange={(e) => setHasEmail(e.target.checked)}
          />
          <span>Has email</span>
        </label>
        <label className="pb-check">
          <input
            type="checkbox"
            name="include_opted_out"
            checked={includeOptedOut}
            onChange={(e) => setIncludeOptedOut(e.target.checked)}
          />
          <span>Include opted-out</span>
        </label>
        {(minBalanceInvalid || oldestDosInvalid || lastOutreachInvalid) && (
          <span className="pb-field-error">
            {minBalanceInvalid
              ? "Enter an amount like 25 or 25.50"
              : "Enter a whole number of days"}
          </span>
        )}
      </div>

      {error && (
        <Message tone="error" testId="pb-error">
          {error}
        </Message>
      )}

      {data && (
        <div className="pb-totals" data-testid="pb-totals">
          <span>
            <strong>{data.totals.patients}</strong> patient
            {data.totals.patients === 1 ? "" : "s"} owing{" "}
            <strong>{formatCents(data.totals.balanceCents)}</strong>
          </span>
          {selected.size > 0 && (
            <span>
              <strong>{selected.size}</strong> selected · {formatCents(selectedBalance)}
            </span>
          )}
          {data.truncated && (
            <span className="pb-truncated" data-testid="pb-truncated">
              Showing the first {rows.length} of {data.total} by balance. Narrow the
              filters, or export for the whole list.
            </span>
          )}
          {data.today && <span className="pb-muted">as of {formatDay(data.today)}</span>}
          {selected.size > 0 && (
            <button
              type="button"
              className="btn btn-primary btn-sm pb-send"
              onClick={() => setDialog({ mode: "compose" })}
              disabled={!status}
              title={
                status
                  ? "Pick a template, preview, and send to the selected patients"
                  : "Checking whether texting and email are set up…"
              }
            >
              Send to selected ({selected.size})
            </button>
          )}
        </div>
      )}

      {!loading && !error && data && rows.length === 0 && (
        <p className="pb-empty">
          No patient matches{anyFilterOff ? " these filters" : " the default filters"}.
        </p>
      )}

      {rows.length > 0 && (
        <div className="pb-table-wrap">
          <table className="pb-table">
            <thead>
              <tr>
                <th className="pb-select">
                  <input
                    type="checkbox"
                    aria-label="Select every patient who can be reached"
                    checked={allSelected}
                    disabled={reachable.length === 0}
                    onChange={toggleAll}
                  />
                </th>
                <th>Patient</th>
                <th className="num">Balance</th>
                <th>Oldest DOS</th>
                <th>Last statement</th>
                <th>Last outreach</th>
                <th>Promise</th>
                <th className="num">Payments since outreach</th>
                <th>Channels</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const canSelect = row.canText || row.canEmail;
                const statement = row.lastStatement ? sendStatusChip(row.lastStatement.status) : null;
                const promise = row.promise ? promiseChip(row.promise.state) : null;
                return (
                  <tr
                    key={row.patientId}
                    className={`pb-row${selected.has(row.patientId) ? " is-selected" : ""}${row.optedOut ? " is-opted-out" : ""}`}
                    data-testid="pb-row"
                  >
                    <td className="pb-select">
                      <input
                        type="checkbox"
                        aria-label={`Select ${formatPatientName(row.displayName)}`}
                        checked={selected.has(row.patientId)}
                        disabled={!canSelect}
                        title={
                          canSelect
                            ? undefined
                            : row.canTextReason || row.canEmailReason || "No channel the practice may use"
                        }
                        onChange={() => toggleRow(row.patientId)}
                      />
                    </td>
                    <td>
                      <span className="pb-patient">{formatPatientName(row.displayName)}</span>
                      <div className="pb-muted">
                        {row.openClaims} open claim{row.openClaims === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td className="num pb-amount">{formatCents(row.balanceCents)}</td>
                    <td className="pb-nowrap">{row.oldestDos ? formatDay(row.oldestDos) : "—"}</td>
                    <td>
                      {statement ? (
                        <>
                          <Chip tone={statement.tone}>{statement.label}</Chip>
                          <div className="pb-muted">{formatWhen(row.lastStatement.sentAt)}</div>
                        </>
                      ) : (
                        <span className="pb-muted">Never</span>
                      )}
                    </td>
                    <td>
                      {row.lastOutreach ? (
                        <>
                          <div>
                            {channelLabel(row.lastOutreach.channel)}
                            {row.lastOutreach.kind ? ` · ${row.lastOutreach.kind}` : ""}
                          </div>
                          <div className="pb-muted">{formatWhen(row.lastOutreach.at)}</div>
                        </>
                      ) : (
                        <span className="pb-muted">Never</span>
                      )}
                    </td>
                    <td>
                      {row.promise ? (
                        <>
                          {promise && <Chip tone={promise.tone}>{promise.label}</Chip>}
                          <div className="pb-muted">
                            by {row.promise.by ? formatDay(row.promise.by) : "—"}
                          </div>
                        </>
                      ) : (
                        <span className="pb-muted">—</span>
                      )}
                    </td>
                    <td className="num">
                      {row.paymentsSinceOutreachCount > 0 ? (
                        <>
                          <span className="pb-paid">
                            {formatCents(row.paymentsSinceOutreachCents)}
                          </span>
                          <div className="pb-muted">
                            {row.paymentsSinceOutreachCount} payment
                            {row.paymentsSinceOutreachCount === 1 ? "" : "s"}
                          </div>
                        </>
                      ) : (
                        <span className="pb-muted">—</span>
                      )}
                    </td>
                    <td>
                      <ChannelChips row={row} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CampaignsList
        campaigns={campaigns}
        error={campaignsError}
        onOpen={(c) => setDialog({ mode: "progress", campaignId: c.id, initial: c })}
      />

      {dialog && status && (
        <div
          className="pb-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDialog();
          }}
        >
          {dialog.mode === "compose" ? (
            <CampaignModal
              practiceId={practiceId}
              rows={selectedRows}
              status={status}
              filters={filters}
              onClose={closeDialog}
              onStarted={onStarted}
            />
          ) : (
            <CampaignPanel
              key={dialog.campaignId}
              practiceId={practiceId}
              campaignId={dialog.campaignId}
              initial={dialog.initial}
              onClose={closeDialog}
              onChanged={loadCampaigns}
            />
          )}
        </div>
      )}
    </div>
  );
}
